import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ToastContainer';
import * as api from '../services/api';
import {
  formatBytes,
  formatBytesFromMB,
  formatDuration,
  formatDurationFromMinutes,
} from '../utils/format';
import './Connections.css';

const Connections = () => {
  const { t, i18n } = useTranslation();
  const { user, isCompanyAdmin } = useAuth();
  const { showError, showSuccess } = useToast();
  const [activeTab, setActiveTab] = useState('my');
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [myConnections, setMyConnections] = useState([]);
  const [companyConnections, setCompanyConnections] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [companyStats, setCompanyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);

  const canViewCompany = isCompanyAdmin || user?.is_company_admin;

  useEffect(() => {
    loadConnections();
    loadStats();
    const interval = setInterval(() => {
      loadConnections(false);
      loadStats();
    }, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadConnections = async (withSpinner = true) => {
    if (withSpinner) setLoading(true);
    try {
      if (activeTab === 'my') {
        const response = await api.getUserConnections();
        setMyConnections(response || []);
      } else if (activeTab === 'company' && canViewCompany) {
        const response = await api.getCompanyConnections();
        setCompanyConnections(response || []);
      }
    } catch (error) {
      console.error('Failed to load connections:', error);
    } finally {
      if (withSpinner) setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      if (activeTab === 'my') {
        setMyStats(await api.getMyConnectionStats());
      } else if (activeTab === 'company' && canViewCompany) {
        setCompanyStats(await api.getCompanyConnectionStats());
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const connections = activeTab === 'my' ? myConnections : companyConnections;
  const stats = activeTab === 'my' ? myStats : companyStats;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (connections || []).filter((conn) => {
      const status = String(conn.status || '').toLowerCase();
      const statusOk =
        statusFilter === 'all' ||
        (statusFilter === 'active' && status === 'active') ||
        (statusFilter === 'ended' && status !== 'active');
      if (!statusOk) return false;
      if (!q) return true;
      const hay = [
        conn.user_email,
        conn.username,
        conn.node?.name,
        conn.node_name,
        conn.node?.location,
        conn.node_location,
        conn.ip_address,
        conn.client_ip,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [connections, query, statusFilter]);

  useEffect(() => {
    if (!filtered.length) {
      setSelectedId(null);
      return;
    }
    if (!filtered.some((c) => c.id === selectedId)) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  const selected = filtered.find((c) => c.id === selectedId) || null;

  const handleDisconnect = async (connectionId) => {
    const ok = window.confirm(
      t('connections.disconnectConfirm', 'End this connection session?')
    );
    if (!ok) return;
    setDisconnecting(connectionId);
    try {
      await api.disconnectFromNode(connectionId);
      showSuccess(t('connections.disconnected', 'Connection ended'));
      await loadConnections(false);
      await loadStats();
    } catch (error) {
      console.error('Failed to disconnect:', error);
      showError(t('connections.disconnectError', 'Could not end connection'));
    } finally {
      setDisconnecting(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString(i18n.language || undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const nodeName = (conn) => conn?.node?.name || conn?.node_name || '—';
  const nodeLocation = (conn) => conn?.node?.location || conn?.node_location || '—';
  const nodeFlag = (conn) => conn?.node?.flag_emoji || '';
  const clientIp = (conn) => conn?.ip_address || conn?.client_ip || '—';
  const isActive = (conn) => String(conn?.status || '').toLowerCase() === 'active';

  const activeCount = (connections || []).filter(isActive).length;

  return (
    <div className="cx-page">
      <div className="cx-content">
      <section className="cx-hero">
        <div className="cx-hero-copy">
          <p className="cx-kicker">{t('connections.kicker', 'Sessions')}</p>
          <h1>{t('connections.title', 'Connections')}</h1>
          <p className="cx-lead">
            {t(
              'connections.lead',
              'Live tunnel activity across your account — latency-ready overview of who is connected, where, and for how long.'
            )}
          </p>
          <p className="cx-lead-mobile">
            {t('connections.leadMobile', 'Tap a session to see details.')}
          </p>
          <div className="cx-tabs" role="tablist">
            <button
              type="button"
              className={`cx-tab ${activeTab === 'my' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('my')}
            >
              {t('connections.myConnections', 'My sessions')}
            </button>
            {canViewCompany && (
              <button
                type="button"
                className={`cx-tab ${activeTab === 'company' ? 'is-active' : ''}`}
                onClick={() => setActiveTab('company')}
              >
                {t('connections.companyConnections', 'Company sessions')}
              </button>
            )}
          </div>
        </div>
        <div className="cx-live">
          <div className={`cx-pulse ${activeCount > 0 ? 'is-on' : ''}`} />
          <div>
            <strong>{stats?.active_connections ?? activeCount}</strong>
            <span>{t('connections.activeNow', 'Active now')}</span>
          </div>
        </div>
      </section>

      <section className="cx-stats">
        <article className="cx-stat">
          <span>{t('connections.activeConnections', 'Active')}</span>
          <strong>{stats?.active_connections || 0}</strong>
        </article>
        <article className="cx-stat">
          <span>{t('connections.totalData', 'Total data')}</span>
          <strong>{formatBytesFromMB(stats?.total_data_mb || 0)}</strong>
        </article>
        <article className="cx-stat">
          <span>{t('connections.avgDuration', 'Avg duration')}</span>
          <strong>{formatDurationFromMinutes(stats?.avg_duration_min || 0)}</strong>
        </article>
        {activeTab === 'my' ? (
          <>
            <article className="cx-stat">
              <span>{t('connections.connectionsToday', 'Today')}</span>
              <strong>{stats?.connections_today || 0}</strong>
            </article>
            <article className="cx-stat">
              <span>{t('connections.connectionsThisWeek', 'This week')}</span>
              <strong>{stats?.connections_this_week || 0}</strong>
            </article>
          </>
        ) : (
          <>
            <article className="cx-stat">
              <span>{t('connections.uniqueUsersToday', 'Users today')}</span>
              <strong>{stats?.unique_users_today || stats?.connections_today || 0}</strong>
            </article>
            <article className="cx-stat highlight">
              <span>{t('connections.mostUsedServer', 'Top server')}</span>
              <strong className="cx-stat-sm">{stats?.most_used_server || '—'}</strong>
            </article>
          </>
        )}
      </section>

      <section className="cx-workspace">
        <aside className={`cx-list ${selected ? 'has-selection' : ''}`}>
          <div className="cx-toolbar">
            <div className="cx-filters">
              {[
                { key: 'all', label: t('connections.filter.all', 'All') },
                { key: 'active', label: t('connections.filter.active', 'Active') },
                { key: 'ended', label: t('connections.filter.ended', 'Ended') },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`cx-filter ${statusFilter === item.key ? 'is-active' : ''}`}
                  onClick={() => setStatusFilter(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <input
              className="cx-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('connections.search', 'Search sessions…')}
            />
          </div>

          {loading ? (
            <div className="cx-empty">
              <div className="cx-spinner" />
              <p>{t('connections.loading', 'Loading sessions…')}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="cx-empty">
              <h3>{t('connections.noData', 'No sessions found')}</h3>
              <p>
                {t(
                  'connections.noDataDesc',
                  'Connect from the dashboard to see live tunnel activity here.'
                )}
              </p>
            </div>
          ) : (
            <ul className="cx-session-list">
              {filtered.map((conn) => (
                <li key={conn.id}>
                  <button
                    type="button"
                    className={`cx-session ${selected?.id === conn.id ? 'is-active' : ''} ${
                      isActive(conn) ? 'is-live' : ''
                    }`}
                    onClick={() => setSelectedId(conn.id)}
                  >
                    <div className="cx-session-top">
                      <span className="cx-flag">{nodeFlag(conn) || '◎'}</span>
                      <div className="cx-session-copy">
                        <strong>{nodeName(conn)}</strong>
                        <em>{nodeLocation(conn)}</em>
                      </div>
                      <span className={`cx-chip ${isActive(conn) ? 'live' : 'ended'}`}>
                        {isActive(conn)
                          ? t('connections.status.active', 'Live')
                          : t('connections.status.disconnected', 'Ended')}
                      </span>
                    </div>
                    <div className="cx-session-meta">
                      {activeTab === 'company' && (
                        <span>{conn.user_email || conn.username || '—'}</span>
                      )}
                      <span>{clientIp(conn)}</span>
                      <span>{formatDuration(conn.duration_seconds)}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <footer className="cx-list-footer">
            {t('connections.footer', '{{count}} sessions shown', { count: filtered.length })}
          </footer>
        </aside>

        <article className={`cx-detail ${selected ? 'is-open' : ''}`}>
          {!selected ? (
            <div className="cx-empty soft">
              <h3>{t('connections.select', 'Select a session')}</h3>
              <p>
                {t(
                  'connections.selectDesc',
                  'Pick a tunnel from the left to inspect route, timing, and transfer.'
                )}
              </p>
            </div>
          ) : (
            <>
              <header className="cx-detail-head">
                <button
                  type="button"
                  className="cx-back"
                  onClick={() => setSelectedId(null)}
                >
                  ←
                </button>
                <div>
                  <p className="cx-kicker">
                    {isActive(selected)
                      ? t('connections.liveSession', 'Live session')
                      : t('connections.pastSession', 'Past session')}
                  </p>
                  <h2>
                    {nodeFlag(selected)} {nodeName(selected)}
                  </h2>
                  <p className="cx-detail-sub">{nodeLocation(selected)}</p>
                </div>
                {isActive(selected) && (
                  <button
                    type="button"
                    className="cx-btn danger"
                    disabled={disconnecting === selected.id}
                    onClick={() => handleDisconnect(selected.id)}
                  >
                    {disconnecting === selected.id
                      ? t('connections.ending', 'Ending…')
                      : t('connections.endSession', 'End session')}
                  </button>
                )}
              </header>

              <div className="cx-detail-grid">
                <div>
                  <span>{t('connections.table.status', 'Status')}</span>
                  <strong className={isActive(selected) ? 'ok' : 'muted'}>
                    {isActive(selected)
                      ? t('connections.status.active', 'Live')
                      : t('connections.status.disconnected', 'Ended')}
                  </strong>
                </div>
                <div>
                  <span>{t('connections.table.ipAddress', 'Client IP')}</span>
                  <strong>{clientIp(selected)}</strong>
                </div>
                <div>
                  <span>{t('connections.table.startTime', 'Started')}</span>
                  <strong>{formatDate(selected.connected_at)}</strong>
                </div>
                <div>
                  <span>{t('connections.table.duration', 'Duration')}</span>
                  <strong>{formatDuration(selected.duration_seconds)}</strong>
                </div>
                <div>
                  <span>{t('connections.table.data', 'Transferred')}</span>
                  <strong>
                    {formatBytes((selected.bytes_sent || 0) + (selected.bytes_received || 0))}
                  </strong>
                </div>
                <div>
                  <span>{t('connections.upDown', 'Up / Down')}</span>
                  <strong>
                    {formatBytes(selected.bytes_sent)} / {formatBytes(selected.bytes_received)}
                  </strong>
                </div>
              </div>

              {activeTab === 'company' && (
                <div className="cx-user-card">
                  <span className="cx-avatar">
                    {(selected.user_email || selected.username || 'U').charAt(0).toUpperCase()}
                  </span>
                  <div>
                    <strong>{selected.username || t('connections.table.user', 'User')}</strong>
                    <em>{selected.user_email || '—'}</em>
                  </div>
                </div>
              )}

              <div className="cx-signal">
                <div className="cx-signal-bar">
                  <i style={{ animationDelay: '0s' }} />
                  <i style={{ animationDelay: '0.15s' }} />
                  <i style={{ animationDelay: '0.3s' }} />
                  <i style={{ animationDelay: '0.45s' }} />
                </div>
                <p>
                  {isActive(selected)
                    ? t(
                        'connections.signalLive',
                        'Encrypted tunnel is active. Metrics refresh every 15 seconds.'
                      )
                    : t(
                        'connections.signalEnded',
                        'This tunnel is closed. Historical transfer totals remain available.'
                      )}
                </p>
              </div>
            </>
          )}
        </article>
      </section>
      </div>
    </div>
  );
};

export default Connections;
